import { useNavigate } from '@rocketlane/rocket-router';
import { useBoolean } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { AuthenticationServiceProvider } from 'api/account';
import { triggerOtp } from 'components/pages/two-factor-auth/utils';
import { LS_TEMPLATE_ID_KEY, RoutePaths } from 'constant';
import { useAccountMetaQuery } from 'queries';
import { ActiveTab, ApiKey, getOkta } from 'services';
import { getTokenMeta, PermissionType } from 'utils/token';
import { useQueryParams } from './use-query-params';
// Restores the saved deep link after login. Uses a hard navigation (window.location), NOT
// rocket-router's SPA navigate: post-rocket-router (#17308), an SPA replace/nav here either
// no-ops (the useNavigate activeRef guard) or lands before auth state propagates, so the
// protected-route gate re-bounces to home. A full reload re-reads the fresh session and
// renders the deep link — this is exactly why the cookies flow (loginWithNitro) already
// uses window.location.href. The NavigateFunction param is kept for signature compatibility.
export const redirectToSavedTab = (_history)=>{
    var _ref, _ref1;
    var _this, _this1;
    const savedTab = ActiveTab.pop();
    const pathname = savedTab ? (_ref = (_this = new URL(savedTab)) === null || _this === void 0 ? void 0 : _this.pathname) !== null && _ref !== void 0 ? _ref : '/' : '/';
    const hash = savedTab ? (_ref1 = (_this1 = new URL(savedTab)) === null || _this1 === void 0 ? void 0 : _this1.hash) !== null && _ref1 !== void 0 ? _ref1 : '' : '';
    window.location.replace(`${pathname}${hash}`);
};
export const useLoginWithToken = (token, tokenType)=>{
    var _params_get, _params_get1;
    var _data_okta;
    const history = useNavigate();
    const replace = useNavigate();
    const [isError, setError] = useState(false);
    const [isLoading, { setTrue, setFalse }] = useBoolean(false);
    const params = useQueryParams();
    const externalRefKey = (_params_get = params.get('externalRefKey')) !== null && _params_get !== void 0 ? _params_get : '';
    const projectId = (_params_get1 = params.get('projectId')) !== null && _params_get1 !== void 0 ? _params_get1 : '';
    const { data } = useAccountMetaQuery();
    const isCognitoEnabled = (data === null || data === void 0 ? void 0 : (_data_okta = data.okta) === null || _data_okta === void 0 ? void 0 : _data_okta.authenticationServiceProvider) === AuthenticationServiceProvider.COGNITO;
    const clientId = data === null || data === void 0 ? void 0 : data.okta.clientId;
    // Guards against the effect re-firing after the URL is mutated by post-consume navigation
    // (redirectToSavedTab → token query param drops to ''). Without this, MagicLinkPage re-renders
    // with token='' before unmounting, the effect re-fires, ApiKey.clear() destroys the just-created
    // api-key, and the subsequent ApiKey.create('') 401s — leaving the user logged out.
    const consumed = useRef(false);
    useEffect(()=>{
        // Don't consume an empty token (happens during post-redirect re-render), and don't
        // re-consume after a successful run (defense-in-depth against future re-fire causes).
        if (!token || consumed.current) {
            return;
        }
        const loginWithoutOtp = async ()=>{
            setTrue();
            if (ApiKey.exists()) {
                await ApiKey.clear();
            }
            if (clientId) {
                consumed.current = true;
                try {
                    var _getOkta;
                    /**
           * Check if magic link contains hash to load the task drawer.
           */ await ApiKey.create(token, tokenType, clientId, externalRefKey, projectId);
                    (_getOkta = getOkta()) === null || _getOkta === void 0 ? void 0 : _getOkta.authStateManager.updateAuthState();
                    setFalse();
                    const templateId = localStorage.getItem(LS_TEMPLATE_ID_KEY);
                    localStorage.removeItem(LS_TEMPLATE_ID_KEY);
                    if (isCognitoEnabled && templateId) {
                        replace(RoutePaths.documentTemplate.replace(':templateId', templateId));
                        return;
                    }
                    redirectToSavedTab(history);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                } catch (e) {
                    consumed.current = false;
                    setFalse();
                    setError(true);
                }
            }
        };
        const loginWithOtp = async ()=>{
            try {
                /**
         * Check if magic link contains hash to load the task drawer.
         */ if (ApiKey.exists()) {
                    redirectToSavedTab(history);
                } else {
                    consumed.current = true;
                    triggerOtp(token);
                    history(RoutePaths.magicLink2fa, {
                        state: {
                            token
                        }
                    });
                }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (e) {
                consumed.current = false;
                setError(true);
            }
        };
        const exec = ()=>{
            const { decoded } = getTokenMeta(token);
            const isOTPEnabled = (decoded === null || decoded === void 0 ? void 0 : decoded.permission) === PermissionType.UserOtp || false;
            if (isOTPEnabled) {
                loginWithOtp();
            } else {
                loginWithoutOtp();
            }
        };
        exec();
    }, [
        token,
        setTrue,
        setFalse,
        history,
        tokenType,
        externalRefKey,
        projectId,
        clientId,
        isCognitoEnabled,
        replace
    ]);
    return [
        isLoading,
        isError
    ];
};
